// Toasts (02-building-blocks.md): one short notice at the bottom centre, one at a time; it goes
// after 4 s (5 s with an action) or when closed, and never takes the focus.
import { useEffect, useLayoutEffect, useRef, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { useTopModal } from './Dialog';
import { IconButton } from './Button';
import { icons } from './icons';
import { t } from '../i18n';

interface Toast {
  id: number;
  text: string;
  action?: { label: string; run: () => void };
}

let current: Toast | null = null;
let next = 0;
const listeners = new Set<() => void>();
const set = (t: Toast | null) => {
  current = t;
  listeners.forEach((l) => l());
};

/** Shows a toast, replacing the one showing. */
export function toast(text: string, action?: Toast['action']): void {
  set({ id: ++next, text, action });
}

const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};

export function Toaster() {
  const shown = useSyncExternalStore(subscribe, () => current);
  useEffect(() => {
    if (!shown) return;
    const timer = setTimeout(() => current?.id === shown.id && set(null), shown.action ? 5000 : 4000);
    return () => clearTimeout(timer);
  }, [shown]);
  // In the top layer, inside the modal window on top when one is open (so it shows above it and can
  // be used); it moves when that window closes (a confirmation that showed it, settings).
  const region = useRef<HTMLDivElement>(null);
  const layer = useTopModal(!!shown);
  useLayoutEffect(() => {
    const el = region.current;
    if (shown && el?.isConnected && !el.matches(':popover-open')) el.showPopover?.();
  });
  const content = (
    <div ref={region} className="toast-region" role="status" popover="manual">
      {shown && (
        <div className="toast" key={shown.id}>
          <span>{shown.text}</span>
          {shown.action && (
            <button
              type="button"
              className="toast-action"
              onClick={() => {
                set(null);
                shown.action!.run();
              }}
            >
              {shown.action.label}
            </button>
          )}
          <IconButton label={t('common.close')} icon={icons.close} size={18} tooltip={false} onClick={() => set(null)} />
        </div>
      )}
    </div>
  );
  return layer ? createPortal(content, layer) : content;
}
